"use client";

import { ReactNode, useTransition } from "react";
import { useRouter } from "@/i18n/navigation";
import { Button, ButtonProps } from "@/components/ui/Button";
import { Motion } from "@/components/framer-motion";

interface InteractiveButtonProps
  extends Omit<ButtonProps, "onClick" | "children"> {
  href?: string;
  onClick?: () => void;
  label?: string;
  icon?: ReactNode;
  animate?: boolean;
  revert?: boolean;
  isStatic?: boolean;
  duration?: number;
  delay?: number;
  children?: ReactNode; // 👈 added children prop
  disabled?:boolean
}

export function InteractiveButton({
  href,
  onClick,
  label = "Continue",
  disabled,
  animate = true,
  icon,
  className,
  revert = false,
  isStatic,
  duration = 0.3,
  delay = 0.5,
  children, // 👈 destructure children
  ...rest
}: InteractiveButtonProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const handleClick = (event: any) => {
    event.preventDefault();
    startTransition(() => {
      if (onClick) {
        onClick();
      } else if (href) {
        router.push(href);
      }
    });
  };

  const content = children ?? label; // 👈 prioritize children over label

  return (
    <Button
      onClick={handleClick}
      isLoading={isPending}
      className={className}
      disabled={disabled}
      aria-disabled={disabled}
      isStatic={isStatic}
      href={href}
      {...rest}
    >
      {animate ? (
        <Motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            duration: duration,
            type: "spring",
            stiffness: 110,
            delay: delay,
          }}
          className="flex gap-2 justify-center items-center"
        >
          {!revert && content}
          <span
            className="group-hover:translate-x-1 duration-300 block"
            aria-hidden="true"
          >
            {icon}
          </span>
          {revert && content}
        </Motion.div>
      ) : (
        content
      )}
    </Button>
  );
}
